import React, { useState } from 'react'
import { makeStyles } from '@mui/styles'
import { IconButton, Typography } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  container: {
    display: (props) => (props?.fullWidth ? 'flex' : 'inline-flex'),
    width: (props) => (props?.fullWidth ? '100%' : 'auto'),
    flexDirection: 'column',
  },
  label: {
    ...theme.typography.caption,
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.5),
  },
  spinner: {
    display: 'flex',
    alignItems: 'stretch',
    height: (props) => (props?.size === 'small' ? 40 : 'auto'),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
  },
  button: {
    borderRadius: 0,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,

    padding: (props) =>
      props?.size === 'small' ? theme.spacing(0.25) : theme.spacing(1.5),
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
    '&:disabled': {
      color: theme.palette.action.disabled,
      backgroundColor: 'transparent',
    },
  },
  value: {
    ...theme.typography.body1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    backgroundColor: theme.palette.background.paper,

    margin: (props) => (props?.size === 'small' ? '2px' : '12px'),
    fontSize: (props) => (props?.size === 'small' ? '0.75rem' : undefined),
    lineHeight: (props) => (props?.size === 'small' ? 1 : undefined),

    color: theme.palette.text.primary,
    userSelect: 'none',
  },
  dividerLeft: {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  dividerRight: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
}))

function NumberSpinner(props) {
  const {
    label = '',
    min = 0,
    max = 10000,
    step = 1,
    defaultValue,
    disabled = false,
    value: controlledValue,
    onChange,
    size,
  } = props

  const classes = useStyles(props)
  const [internalValue, setInternalValue] = useState(defaultValue ?? min)

  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : internalValue
  const isSmall = size === 'small'

  const handleChange = (newValue) => {
    const clampedValue = Math.min(Math.max(newValue, min), max)
    if (!isControlled) setInternalValue(clampedValue)
    if (onChange) onChange(clampedValue)
  }

  return (
    <div
      className={classes.container}
      style={{
        width: props?.fullWidth ? '100%' : 'fit-content',
        maxWidth: '100%',
      }}
    >
      {label && <Typography className={classes.label}>{label}</Typography>}
      <div
        className={classes.spinner}
        style={{
          width: props?.fullWidth ? '100%' : 'fit-content',
          maxWidth: isSmall ? 88 : 160,
        }}
      >
        <IconButton
          size="small"
          className={clsx(classes.button, classes.dividerRight)}
          onClick={() => handleChange(value - step)}
          disabled={value <= min || disabled}
        >
          <Remove sx={{ fontSize: 16 }} />
        </IconButton>

        <div
          className={classes.value}
          style={{
            paddingInline: isSmall ? 3 : 12,
            minWidth: isSmall ? 30 : 60,
            maxWidth: isSmall ? 48 : 80,
          }}
        >
          {value}
        </div>

        <IconButton
          size="small"
          className={clsx(classes.button, classes.dividerLeft)}
          onClick={() => handleChange(value + step)}
          disabled={value >= max || disabled}
        >
          <Add sx={{ fontSize: 16 }} />
        </IconButton>
      </div>
    </div>
  )
}

export default NumberSpinner
